import { Link } from "react-router-dom";
import { Instagram, Phone, MapPin, Clock, ArrowUpRight, Lock } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { telLink, waLink } from "@/lib/api";
import { Logo } from "./Primitives";

const LEGAL = [
  ["kvkk-aydinlatma-metni", "KVKK Aydınlatma Metni"],
  ["cerez-politikasi", "Çerez Politikası"],
  ["gizlilik-politikasi", "Gizlilik Politikası"],
  ["acik-riza-metni", "Açık Rıza Metni"],
  ["kullanim-kosullari", "Kullanım Koşulları"],
];

const AgencyCredit = () => (
  <div className="rounded-lg border border-[#D4AF37]/25 bg-[#1F1411] p-6 sm:p-8 grid md:grid-cols-[1fr_auto] gap-6 items-center" data-testid="footer-agency-block">
    <div>
      <p className="font-display text-[#D4AF37] tracking-[0.18em] text-sm sm:text-base" data-testid="footer-agency-credit">
        Bu proje Ektiren Project'e aittir.
      </p>
      <p className="mt-3 text-sm text-[#CBBBAE] leading-relaxed max-w-2xl">
        Ektiren Project; KKTC merkezli, dört dilde (TR / EN / AR / HE) hizmet veren butik bir dijital ajanstır.
        Profesyonel web sitesi tasarımı, SEO optimizasyonu, kurumsal kimlik ve mobil uygulama geliştirme ile
        markanızı büyüten web deneyimleri tasarlar.
      </p>
      <p className="mt-3 text-xs text-[#CBBBAE]/80">
        İletişim: <a href="tel:+905391171883" className="hover:text-[#D4AF37]" data-testid="footer-agency-phone">+90 539 117 1883</a>
        {" · "}
        <a href="mailto:ektiren511@gmail.com" className="hover:text-[#D4AF37]" data-testid="footer-agency-email">ektiren511@gmail.com</a>
      </p>
    </div>
    <a href="https://ektirenproject.com" target="_blank" rel="noopener noreferrer" className="btn-ghost-gold whitespace-nowrap" data-testid="footer-agency-link">
      ektirenproject.com <ArrowUpRight size={16} />
    </a>
  </div>
);

export function Footer() {
  const { settings } = useSite();
  const year = new Date().getFullYear();
  return (
    <footer className="ym-dark relative border-t border-[#D4AF37]/15" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 pt-20 pb-10">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <div className="flex items-center gap-4">
              <Logo size={72} />
              <div>
                <p className="font-display text-xl tracking-[0.14em]">YENİ MERCAN</p>
                <p className="eyebrow !text-[10px] mt-1">Restoran · Buca</p>
              </div>
            </div>
            <p className="mt-6 text-sm text-[#CBBBAE] leading-relaxed max-w-sm">
              Kahvaltı, ev yemekleri, çorba, kokoreç ve premium ızgaralar. Lezzet 7/24 burada!
            </p>
            <a href={`https://instagram.com/${settings.instagram}`} target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm text-[#D4AF37] hover:text-[#E5C158]" data-testid="footer-instagram-link">
              <Instagram size={16} /> @{settings.instagram}
            </a>
          </div>
          <div className="md:col-span-2">
            <p className="eyebrow mb-5">Keşfet</p>
            <ul className="space-y-3 text-sm text-[#CBBBAE]">
              <li><Link to="/menu" className="hover:text-[#D4AF37]" data-testid="footer-menu-link">Dijital Menü</Link></li>
              <li><a href="/#rezervasyon" className="hover:text-[#D4AF37]" data-testid="footer-reservation-link">Rezervasyon</a></li>
              <li><a href="/#galeri" className="hover:text-[#D4AF37]" data-testid="footer-gallery-link">Galeri</a></li>
              <li><a href={waLink(settings.whatsapp)} target="_blank" rel="noopener noreferrer" className="hover:text-[#D4AF37]" data-testid="footer-whatsapp-link">WhatsApp</a></li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow mb-5">Yasal</p>
            <ul className="space-y-3 text-sm text-[#CBBBAE]">
              {LEGAL.map(([slug, label]) => (
                <li key={slug}><Link to={`/yasal/${slug}`} className="hover:text-[#D4AF37]" data-testid={`footer-legal-${slug}`}>{label}</Link></li>
              ))}
              <li>
                <button onClick={() => window.dispatchEvent(new Event("open-cookie-settings"))} className="hover:text-[#D4AF37]" data-testid="footer-cookie-settings-btn">
                  Çerez Tercihleri
                </button>
              </li>
            </ul>
          </div>
          <div className="md:col-span-3">
            <p className="eyebrow mb-5">İletişim</p>
            <ul className="space-y-4 text-sm text-[#CBBBAE]">
              <li className="flex gap-3"><MapPin size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />{settings.address}</li>
              <li className="flex gap-3"><Phone size={16} className="text-[#D4AF37] shrink-0 mt-0.5" /><a href={telLink(settings.phone)} className="hover:text-[#D4AF37]" data-testid="footer-phone-link">{settings.phone}</a></li>
              <li className="flex gap-3"><Clock size={16} className="text-[#D4AF37] shrink-0 mt-0.5" />{settings.hours}</li>
            </ul>
          </div>
        </div>

        <div className="gold-rule my-12" />
        <AgencyCredit />

        <div className="mt-10 flex flex-col md:flex-row md:items-center justify-between gap-4 text-xs text-[#CBBBAE]/70" data-testid="footer-imprint">
          <p>
            © {year} {settings.name}. Tüm hakları saklıdır.
            <span className="mx-2">·</span>
            <Link to="/admin/login" rel="nofollow" className="inline-flex items-center gap-1 hover:text-[#D4AF37]" data-testid="footer-admin-login-link">
              <Lock size={11} /> Yönetici Girişi
            </Link>
          </p>
          <p className="md:text-right">
            {settings.company_title} · {settings.company_tax} · MERSİS: {settings.company_mersis}
          </p>
        </div>
      </div>
    </footer>
  );
}
